import { useMemo } from 'react';
import type { CrewAssignmentStatus } from '@/common/infrastructure/gql/generated/schema';
import type { CrewField } from '../RaptorJobCreateModal';
import type { CrewMemberListItem } from '@/raptorjob/presentation/utils/crewRoleAssignment';
import { crewMemberHasJobRole } from '@/raptorjob/presentation/utils/crewRoleAssignment';

export interface CrewEmployeeOption {
  id: number;
  name: string;
  label: string;
  value: number;
  status: CrewAssignmentStatus[];
}

export interface CrewRowEmployeeAutocompleteState {
  optionsWithCurrentEmployee: CrewEmployeeOption[];
  selectedValue: CrewEmployeeOption | null;
}

function computeCrewRowEmployeeAutocompleteState(
  crew: CrewField,
  employeeOptions: CrewEmployeeOption[],
  crewMembers: CrewMemberListItem[] | null | undefined,
  loadingEmployees: boolean
): CrewRowEmployeeAutocompleteState {
  let optionsWithCurrentEmployee = employeeOptions;
  let selectedValue = employeeOptions.find((option) => option.id === crew.employeeId) || null;

  const memberRecord = crewMembers?.find((e) => e.id === crew.employeeId);
  const definitelyWrongRole =
    !loadingEmployees &&
    (crewMembers?.length ?? 0) > 0 &&
    memberRecord != null &&
    crew.roleId != null &&
    !crewMemberHasJobRole(memberRecord, crew.roleId);

  if (crew.employeeId != null && crew.employeeName && !selectedValue && !definitelyWrongRole) {
    const syntheticOption: CrewEmployeeOption = {
      id: crew.employeeId,
      name: crew.employeeName,
      label: crew.employeeName,
      value: crew.employeeId,
      status: (crew.status || []) as CrewAssignmentStatus[]
    };
    optionsWithCurrentEmployee = [...employeeOptions, syntheticOption];
    selectedValue = syntheticOption;
  }

  return { optionsWithCurrentEmployee, selectedValue };
}

export function useCrewRowEmployeeAutocompleteState(
  crew: CrewField,
  employeeOptions: CrewEmployeeOption[],
  crewMembers: CrewMemberListItem[] | null | undefined,
  loadingEmployees: boolean
): CrewRowEmployeeAutocompleteState {
  return useMemo(
    () => computeCrewRowEmployeeAutocompleteState(crew, employeeOptions, crewMembers, loadingEmployees),
    [crew.employeeId, crew.employeeName, crew.roleId, crew.status, employeeOptions, crewMembers, loadingEmployees]
  );
}
